import { create } from 'zustand';

import type {
  PriceIntelBriefing,
  PriceIntelCandidateFact,
  PriceIntelCandidateUpdateInput,
  PriceIntelIngestionJob,
  PriceIntelManualCandidateInput,
  PriceIntelPriceFact,
  PriceIntelProductSpec,
  PriceIntelSourceRecord,
  PriceIntelTrendResponse,
} from '../types';
import {
  approveCandidate,
  clearDemoData,
  createBriefing,
  createManualCandidate,
  createSourceFromText,
  createSourceFromUpload,
  fetchBriefings,
  fetchCandidates,
  fetchJobs,
  fetchPriceFacts,
  fetchTrends,
  rejectCandidate,
  seedDemoData,
  updateCandidate,
} from '../services/price-intel-service';
import { getPriceIntelRuntimeMode, setPriceIntelRuntimeMode } from '../services/price-intel-mode';
import { demoProductSpecs } from '../fixtures/demo-product-specs';

interface PriceIntelState {
  mode: 'local' | 'api';
  sources: PriceIntelSourceRecord[];
  jobs: PriceIntelIngestionJob[];
  candidates: PriceIntelCandidateFact[];
  facts: PriceIntelPriceFact[];
  trends: PriceIntelTrendResponse | null;
  briefings: PriceIntelBriefing[];
  productSpecs: PriceIntelProductSpec[];
  isLoading: boolean;
  error: string | null;
  setMode: (mode: 'local' | 'api') => Promise<void>;
  refreshAll: () => Promise<void>;
  createSourceFromFiles: (files: File[]) => Promise<void>;
  createSourceFromText: (payload: { title?: string; input_text: string }) => Promise<void>;
  createManualCandidate: (payload: PriceIntelManualCandidateInput) => Promise<PriceIntelCandidateFact>;
  seedDemoData: () => Promise<void>;
  clearDemoData: () => Promise<void>;
  fetchJobs: (status?: string) => Promise<void>;
  fetchCandidates: (reviewStatus?: string) => Promise<void>;
  fetchFacts: (filters?: { competitor?: string; region?: string; country?: string; year_quarter?: string }) => Promise<void>;
  fetchTrends: (filters?: { competitor?: string; region?: string; country?: string }) => Promise<void>;
  fetchBriefings: () => Promise<void>;
  updateCandidate: (candidateFactId: string, payload: PriceIntelCandidateUpdateInput) => Promise<void>;
  approveCandidate: (candidateFactId: string) => Promise<void>;
  rejectCandidate: (candidateFactId: string, reviewComment?: string) => Promise<void>;
  createBriefing: (payload: { title: string; reporting_period?: string; competitor?: string; region?: string; country?: string }) => Promise<void>;
  addProductSpecs: (specs: PriceIntelProductSpec[]) => void;
}

export const usePriceIntelStore = create<PriceIntelState>((set, get) => ({
  mode: getPriceIntelRuntimeMode(),
  sources: [],
  jobs: [],
  candidates: [],
  facts: [],
  trends: null,
  briefings: [],
  productSpecs: [],
  isLoading: false,
  error: null,

  setMode: async (mode) => {
    setPriceIntelRuntimeMode(mode);
    set({
      mode,
      error: null,
      sources: [],
      jobs: [],
      candidates: [],
      facts: [],
      trends: null,
      briefings: [],
      productSpecs: [],
    });
    await get().refreshAll();
  },

  refreshAll: async () => {
    await Promise.all([
      get().fetchJobs(),
      get().fetchCandidates('pending_review'),
      get().fetchFacts(),
      get().fetchTrends(),
      get().fetchBriefings(),
    ]);
  },

  createSourceFromFiles: async (files) => {
    try {
      set({ isLoading: true, error: null });
      const createdSources: PriceIntelSourceRecord[] = [];
      for (const file of files) {
        const result = await createSourceFromUpload(file, {
          fileName: file.name,
          metadata: {
            intake: 'dashboard_upload',
          },
        });
        createdSources.push(result.source_record);
      }
      set({ sources: createdSources });
      await get().refreshAll();
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  createSourceFromText: async (payload) => {
    try {
      set({ isLoading: true, error: null });
      const result = await createSourceFromText({
        title: payload.title,
        input_text: payload.input_text,
        metadata: {
          intake: 'dashboard_text',
        },
      });
      set({ sources: [result.source_record] });
      await get().refreshAll();
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  createManualCandidate: async (payload) => {
    try {
      set({ isLoading: true, error: null });
      const candidate = await createManualCandidate(payload);
      await get().refreshAll();
      return candidate;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  seedDemoData: async () => {
    try {
      set({ isLoading: true, error: null });
      await seedDemoData();
      // 同时注入产品规格演示数据
      get().addProductSpecs(demoProductSpecs);
      await get().refreshAll();
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  clearDemoData: async () => {
    try {
      set({ isLoading: true, error: null });
      await clearDemoData();
      set({
        sources: [],
        jobs: [],
        candidates: [],
        facts: [],
        trends: null,
        briefings: [],
        productSpecs: [],
      });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchJobs: async (status) => {
    try {
      set({ isLoading: true, error: null });
      const jobs = await fetchJobs(status);
      set({ jobs });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCandidates: async (reviewStatus = 'pending_review') => {
    try {
      set({ isLoading: true, error: null });
      const candidates = await fetchCandidates(reviewStatus);
      set({ candidates });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchFacts: async (filters) => {
    try {
      set({ isLoading: true, error: null });
      const facts = await fetchPriceFacts(filters);
      set({ facts });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchTrends: async (filters) => {
    try {
      set({ isLoading: true, error: null });
      const trends = await fetchTrends(filters);
      set({ trends });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchBriefings: async () => {
    try {
      set({ isLoading: true, error: null });
      const briefings = await fetchBriefings();
      set({ briefings });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  updateCandidate: async (candidateFactId, payload) => {
    try {
      set({ isLoading: true, error: null });
      const updated = await updateCandidate(candidateFactId, payload);
      set((state) => ({
        candidates: state.candidates.map((item) => (item.id === candidateFactId ? updated : item)),
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  approveCandidate: async (candidateFactId) => {
    try {
      set({ isLoading: true, error: null });
      await approveCandidate(candidateFactId);
      await get().fetchCandidates('pending_review');
      await get().fetchFacts();
      await get().fetchTrends();
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  rejectCandidate: async (candidateFactId, reviewComment) => {
    try {
      set({ isLoading: true, error: null });
      await rejectCandidate(candidateFactId, reviewComment);
      await get().fetchCandidates('pending_review');
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  createBriefing: async (payload) => {
    try {
      set({ isLoading: true, error: null });
      await createBriefing(payload);
      await get().fetchBriefings();
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  addProductSpecs: (specs) => {
    set((state) => ({
      productSpecs: [
        ...state.productSpecs,
        ...specs.filter((s) => !state.productSpecs.some((existing) => existing.temp_id === s.temp_id)),
      ],
    }));
  },
}));
